// docs/113: a quest's path, Slay-the-Spire fashion. The stops run left to right over the quest's own
// ground (`map/questPath.ts`, drawn in its scene: a castle's halls, an inn's cellar, the open desert):
// one encounter, a choice of two, (a main quest: a second choice of two), the boss, the reward. The
// lead clicks a lit stop; the band walks the road single file behind its crowned leader (the chart's
// `bandLine`), and once the leader reaches the stop the map zooms into it and fades to black
// (`core/stores/pathCurtain`), and the party is in the encounter. There is no going back, and no
// rest on the road: the party panel shows the wounds every hero carries to the next stop
// (`quest_wound`). The chart's controls are all here: the camera (wheel, pinch, WASD, Q/E, V),
// right-click pings for the party, the emote wheel on every token, the dock (chat, the Chronicle,
// who is here, the Ledger) and my heroes' band (stats card, inventory with its trades, level-ups).
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables, type EventContext } from '../../../bindings';
import type { Identity } from 'spacetimedb';
import { EmoteAnchor } from '../../emote/EmoteBubble';
import { AlmanacChip, Badge, Button, DialogueBox, HeroSprite, Meter, OryxIcon, Panel, PingMark, confirmDialog } from '../../components';
import { useSession } from '../../../core/stores/session';
import { useNames, useParty, usePartyMembers, useWorldClock, useWorldHeroes } from '../../../core/subscriptions/hooks';
import { getConn, identityHex } from '../../../core/net/connection';
import { rpc, useToasts } from '../../../core/net/reducers';
import { CLASS_NAMES } from '../../../core/sim/adapt';
import { INST, isPath, openNeighbours, siteById, stillAhead, STOP, stopKind, type Adventure, type Scene, type Site } from '../../../core/adventure/graph';
import { CURTAIN_CLOSE_MS, CURTAIN_HOLD_MS, CURTAIN_OPEN_MS, PATH_HOLD_MAX_MS, usePathCurtain } from '../../../core/stores/pathCurtain';
import { useRoute } from '../../../core/stores/route';
import { parseSeasonKey, seasonKey } from '../../../core/story/calendar';
import { playSfx } from '../../../core/audio';
import { deviceNow } from '../../../core/time';
import { PING_MS, PING_SOUND, mayPing } from '../../../core/pings';
import { RoadDots } from '../map/RoadDots';
import { MapCanvas } from '../map/MapCanvas';
import { useOverworldCamera } from '../map/useOverworldCamera';
import { BAND_SHOWN, bandLine, knotSpot } from '../map/bandWalk';
import { pathLength, WALK_CELLS_PER_S } from '../map/overworld';
import { generatePath, pathFrame, pathRoute, QP_H, QP_W, SCENE_WORDS, type PathBoard } from '../map/questPath';
import { playerColour, usePlayerColours } from '../ChatDock';
import { ShellMenuButton, ShellName, ShellNav } from '../Shell';
import { Dock } from '../World';
import { TownBand, type BandHero } from '../village/Band';
import '../village/talk.css';
import './questpath.css';

type Instance = NonNullable<ReturnType<typeof import('../../../core/subscriptions/hooks').useInstance>>;
type HeroRow = ReturnType<typeof useWorldHeroes>[number];

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const SMALL_WORDS = new Set(['of', 'the', 'a', 'an', 'and', 'to', 'in', 'on', 'at', 'by']);
const titled = (s: string) => s.split(' ').map((w, i) => (i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');

/** A quest's path when its blob is one (docs/113); old adventures keep their site map. */
export const walksAPath = (a: Adventure): boolean => isPath(a.siteGraph);

// ---- wounds and pings ------------------------------------------------------------------------

/** docs/113 QP4: what each hero has lost on this path so far. */
function useQuestWounds(instanceId: bigint) {
  return useTable(tables.questWound.where((r) => r.instanceId.eq(instanceId)))[0];
}

export interface PathPing { id: number; owner: string; x: number; y: number; kind: number; at: number }
interface PingRow { instanceId: bigint; owner: Identity; x: number; y: number; kind: number }
let pingSeq = 1;

/** docs/113 QP8: the party's pings on this path (`quest_ping`), a few seconds each, as the chart's. */
function useQuestPings(instanceId: bigint): PathPing[] {
  const [pings, setPings] = useState<PathPing[]>([]);
  useEffect(() => {
    const c = getConn();
    if (!c) return;
    const sub = c.subscriptionBuilder()
      .onError((_ctx, err?: Error) => console.error('[path ping] subscription error', err))
      .subscribe([tables.questPing.where((r) => r.instanceId.eq(instanceId))]);
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const onRow = (_ctx: EventContext, row: PingRow) => {
      if (row.instanceId !== instanceId) return;
      const p: PathPing = { id: pingSeq++, owner: identityHex(row.owner), x: row.x / 10, y: row.y / 10, kind: row.kind, at: deviceNow() };
      setPings((xs) => [...xs.filter((q) => q.at > p.at - PING_MS), p]);
      playSfx(PING_SOUND[row.kind] ?? 'ping.look');
      const t = setTimeout(() => { timers.delete(t); setPings((xs) => xs.filter((q) => q.id !== p.id)); }, PING_MS);
      timers.add(t);
    };
    c.db.questPing.onInsert(onRow);
    return () => { c.db.questPing.removeOnInsert(onRow); sub.unsubscribe(); for (const t of timers) clearTimeout(t); };
  }, [instanceId]);
  return pings;
}

// ---- the band on the road ---------------------------------------------------------------------

/**
 * The party on the path (docs/113 QP7): at rest a knot over the stop it stands at; when the lead
 * sends it on, single file along the road, the lead player's first hero crowned at the head, each
 * follower `BAND_GAP` behind. `onArrive` fires once when the leader reaches the stop.
 */
function PathBand({ board, edges, current, heroes, leadOwner, colours, me, onArrive }: {
  board: PathBoard; edges: Adventure['siteGraph']['edges']; current: number; heroes: readonly PathHero[]; leadOwner?: string;
  colours: Map<string, number>; me: string; onArrive: (site: number, walked: boolean) => void;
}) {
  const tokens = useRef<Array<HTMLSpanElement | null>>([]);
  const was = useRef<number | undefined>(undefined);
  const arrive = useRef(onArrive);
  arrive.current = onArrive;
  const order = [...heroes].sort((a, b) => {
    const la = identityHex(a.owner) === leadOwner ? 0 : 1, lb = identityHex(b.owner) === leadOwner ? 0 : 1;
    return la - lb || (a.id < b.id ? -1 : 1);
  });
  const shown = order.slice(0, BAND_SHOWN);
  const n = shown.length;
  useLayoutEffect(() => {
    const to = board.nodes.get(current);
    if (!to) return;
    const put = (i: number, x: number, y: number, dx: number, moving: boolean) => {
      const el = tokens.current[i];
      if (!el) return;
      el.style.setProperty('--x', x.toFixed(3));
      el.style.setProperty('--y', y.toFixed(3));
      el.style.zIndex = String(Math.round(y * 10));
      if (dx) el.dataset.face = dx < 0 ? 'left' : 'right';
      el.dataset.walking = moving ? 'true' : 'false';
    };
    const rest = () => { for (let i = 0; i < n; i++) { const k = knotSpot(to, i); put(i, k.x, k.y, 0, false); } };
    const from = was.current;
    was.current = current;
    const route = from !== undefined && from !== current ? pathRoute(board, edges, from, current) : undefined;
    if (!route) { rest(); arrive.current(current, false); return; }
    const len = pathLength(route);
    const fromAt = board.nodes.get(from!);
    const start = performance.now();
    let arrived = false, raf = 0;
    const tick = () => {
      const walked = ((performance.now() - start) / 1000) * WALK_CELLS_PER_S;
      if (!arrived && walked >= len) { arrived = true; arrive.current(current, true); }
      if (walked - (n - 1) * 1.15 >= len) { rest(); return; }
      bandLine({ route, tailLen: 0 }, to, fromAt, n, walked).forEach((h, i) => put(i, h.x, h.y, h.dx, h.moving));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [board, edges, current, n]);
  return (
    <div className="fk-path__band" data-testid="map-party" data-site={current}>
      {shown.map((h, i) => {
        const owner = identityHex(h.owner);
        const lead = i === 0 && !!leadOwner && owner === leadOwner;
        const more = i === n - 1 ? order.slice(BAND_SHOWN) : [];
        return (
          <span key={h.id.toString()} ref={(el) => { tokens.current[i] = el; }} className={`fk-world__token ${lead ? 'fk-world__token--lead' : 'fk-world__token--follow'}`}
            style={{ ['--fk-seat' as string]: playerColour(colours.get(owner)) } as CSSProperties} data-testid="map-token" data-hero-name={h.name} data-lead={lead ? 'true' : undefined}>
            {lead && <span className="fk-world__token-crown" title={`${h.name} leads the band`} data-testid="map-token-crown"><OryxIcon name="item_149" scale={1} /></span>}
            <span className="fk-world__token-tag">{h.name}</span>
            <span className="fk-world__token-body"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={2} title={h.name} /></span>
            {more.length > 0 && <span className="fk-world__token-more">+{more.length}<EmoteAnchor heroes={more.map((x) => ({ id: x.id, name: x.name, mine: identityHex(x.owner) === me }))} /></span>}
            <EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: owner === me }]} />
          </span>
        );
      })}
    </div>
  );
}

// ---- the map ----------------------------------------------------------------------------------

type NodeState = 'here' | 'open' | 'cleared' | 'ahead' | 'passed';

function nodeState(s: Site, inst: PathInst, open: readonly number[], ahead: Set<number>): NodeState {
  if (s.id === inst.currentSite) return 'here';
  if (open.includes(s.id)) return 'open';
  if (inst.visited.includes(s.id)) return 'cleared';
  return ahead.has(s.id) ? 'ahead' : 'passed';
}

/** One stop: its framed icon (bigger for the boss and the reward), the kind under it, its name. */
function StopNode({ s, at, state, cleared, clickable, selected, onPick, onHover }: {
  s: Site; at: { x: number; y: number }; state: NodeState; cleared: boolean; clickable: boolean; selected: boolean;
  onPick: () => void; onHover: (on: boolean) => void;
}) {
  const kind = stopKind(s);
  const k = STOP[kind];
  const label = `${titled(s.name)} — ${k.label}${state === 'open' ? ', open' : state === 'here' ? ', the party is here' : state === 'passed' ? ', passed by' : cleared ? ', cleared' : ''}`;
  return (
    <div className={`fk-path__stop fk-path__stop--${kind} fk-path__stop--${state} ${selected ? 'fk-path__stop--sel' : ''} ${clickable ? 'fk-path__stop--go' : ''}`}
      style={{ ['--x' as string]: at.x, ['--y' as string]: at.y } as CSSProperties} data-stop={s.id}>
      <button type="button" className="fk-path__stop-hit fk-hit" onClick={onPick} onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)}
        onFocus={() => onHover(true)} onBlur={() => onHover(false)} aria-label={label} title={label} aria-disabled={!clickable}
        data-testid="site-node" data-site-id={s.id} data-site-kind={s.kind} data-stop-kind={kind} data-state={state} data-open={state === 'open'} data-current={state === 'here'} data-cleared={cleared}>
        <span className="fk-path__stop-icon">
          <OryxIcon name={k.icon} scale={kind === 'boss' || kind === 'reward' ? 3 : 2} glyph={k.label.charAt(0)} />
          {cleared && <span className="fk-path__stop-check" aria-hidden="true">✓</span>}
        </span>
        <span className="fk-path__stop-kind">{k.label}</span>
        <span className="fk-path__stop-name">{titled(s.name)}</span>
      </button>
    </div>
  );
}

/** What the map reads of the instance (the `?path=1` gallery passes its own). */
export type PathInst = Pick<Instance, 'id' | 'currentSite' | 'visited' | 'cleared'>;
export type PathHero = Pick<HeroRow, 'id' | 'owner' | 'name' | 'oryxFrame' | 'class' | 'species'>;

export function PathMap({ a, inst, board, open, ahead, canTravel, busy, selected, onSelect, onTravel, party, leadOwner, colours, me, names, pings, zoomRef, onArrive }: {
  a: Adventure; inst: PathInst; board: PathBoard; open: readonly number[]; ahead: Set<number>; canTravel: boolean; busy: boolean;
  selected: number | undefined; onSelect: (id: number | undefined) => void; onTravel: (id: number) => void;
  party: readonly PathHero[]; leadOwner?: string; colours: Map<string, number>; me: string;
  names: Record<string, string>; pings: readonly PathPing[];
  zoomRef: React.RefObject<HTMLDivElement | null>; onArrive: (site: number, walked: boolean) => void;
}) {
  const g = a.siteGraph;
  const mapEl = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  // docs/42 as on the chart: wheel / pinch / Q–E zoom, WASD / drag / edge pans, V home (the whole path,
  // framed between the bar, the right column and the dialogue)
  const fit = useMemo(() => ({ box: pathFrame(board.nodes, 48), inset: { l: 24, r: 340, t: 120, b: 150 } }), [board]);
  useOverworldCamera(mapEl, stage, { skip: '.fk-path__panel, .fk-world__dock, .fk-town-band, .fk-path__foot, .fk-path__bar', fit });
  // docs/113 QP8: right-click pings the path for the party (Shift: a warning), as the chart's
  useEffect(() => {
    const map = mapEl.current;
    if (!map) return;
    const onMenu = (e: MouseEvent) => {
      const st = stage.current;
      if (!st || (e.target as Element | null)?.closest?.('.fk-path__panel, .fk-world__dock, .fk-town-band, .fk-path__bar, .fk-path__foot, .fk-modal')) return;
      e.preventDefault();
      const r = st.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * QP_W, y = ((e.clientY - r.top) / r.height) * QP_H;
      if (x < 0 || y < 0 || x > QP_W || y > QP_H || !mayPing()) return;
      void rpc.pingQuest(inst.id, Math.round(x * 10), Math.round(y * 10), e.shiftKey ? 2 : 0);
    };
    map.addEventListener('contextmenu', onMenu);
    return () => map.removeEventListener('contextmenu', onMenu);
  }, [inst.id]);
  const travelled = (e: { a: number; b: number }) => inst.visited.includes(e.a) && inst.visited.includes(e.b) && (inst.cleared.includes(e.a) || e.a === g.start);
  return (
    <div ref={mapEl} className="fk-path__map" aria-label={`The way through ${a.location}`}>
      <div ref={zoomRef} className="fk-path__zoom">
        <div ref={stage} className={`fk-path__stage ${board.indoor ? 'fk-path__stage--indoor' : ''}`} style={{ ['--ow-w' as string]: QP_W, ['--ow-h' as string]: QP_H } as CSSProperties}
          data-testid="node-map" data-kit={board.kit} data-current-site={inst.currentSite}>
          <MapCanvas rows={board.rows} seed={Number(inst.id % 2147483647n) + 7} kit={board.kit} fills={board.fills} props={board.props} scale={2} animate />
          {/* the ground steps back a little so the path reads first */}
          <div className="fk-path__veil" aria-hidden="true" />
          <svg className="fk-roads" viewBox={`0 0 ${QP_W * 24} ${QP_H * 24}`} aria-hidden="true">
            {g.edges.map((e, i) => {
              const path = board.paths[i];
              if (!path || path.length < 2) return null;
              const lit = e.a === inst.currentSite && open.includes(e.b);
              const dead = !ahead.has(e.b) && !travelled(e);
              const cls = `fk-path__road ${lit ? 'fk-roads__road--lit fk-path__road--open' : ''} ${travelled(e) ? 'fk-path__road--walked' : ''} ${dead ? 'fk-roads__road--dim fk-path__road--dead' : ''}`;
              // a trodden band under the dots, so the path's fork reads over any ground
              return (
                <g key={i} className={cls} data-testid="map-edge">
                  <polyline className="fk-path__road-bed" points={path.map(([x, y]) => `${x * 24},${y * 24}`).join(' ')} />
                  <RoadDots path={path} skip={20} step={9} size={5} />
                </g>
              );
            })}
          </svg>
          {g.sites.map((s) => {
            const at = board.nodes.get(s.id);
            if (!at) return null;
            const state = nodeState(s, inst, open, ahead);
            const clickable = state === 'open' && canTravel && !busy;
            return <StopNode key={s.id} s={s} at={at} state={state} cleared={inst.cleared.includes(s.id) || (s.id === g.start && inst.visited.length > 1)} clickable={clickable} selected={selected === s.id}
              onPick={() => { onSelect(s.id); if (clickable) onTravel(s.id); }} onHover={(on) => onSelect(on ? s.id : undefined)} />;
          })}
          <PathBand board={board} edges={g.edges} current={inst.currentSite} heroes={party} leadOwner={leadOwner} colours={colours} me={me} onArrive={onArrive} />
          {pings.map((p) => <PingMark key={p.id} kind={p.kind} colour={playerColour(colours.get(p.owner))} who={p.owner === me ? 'You' : names[p.owner] ?? 'Someone'} owner={p.owner} className="fk-ow-ping" style={{ ['--x' as string]: p.x, ['--y' as string]: p.y } as CSSProperties} testId="map-ping" />)}
        </div>
      </div>
    </div>
  );
}

// ---- the screen ---------------------------------------------------------------------------------

/** The storyteller's line at a stop (docs/113): where the path goes from here. */
function stopLine(here: Site | undefined, g: Adventure['siteGraph'], inst: Instance, open: readonly number[]): string {
  if (!here) return '';
  const next = open.map((id) => siteById(g, id)).filter((s): s is Site => !!s);
  const way = (s: Site) => `${STOP[stopKind(s)].label.toLowerCase()} at ${s.name}`;
  const kind = stopKind(here);
  if (kind === 'gate') return `The way in begins at ${cap(here.name)}. Ahead lies ${next.map(way).join(' or ')}. There will be no rest until it is done.`;
  if (kind === 'reward') return `${cap(here.name)}. It is done.`;
  if (!inst.cleared.includes(here.id)) return `The party fell back from ${here.name} to catch its breath. Whatever waits there waits still.`;
  if (next.length === 2) return `${cap(here.name)} is behind you. The way forks: ${way(next[0]!)}, or ${way(next[1]!)}. Choose — there is no going back.`;
  if (next.length === 1 && stopKind(next[0]!) === 'boss') return `${cap(here.name)} is behind you. Ahead, at ${next[0]!.name}, the quest's own foe is waiting.`;
  if (next.length === 1 && stopKind(next[0]!) === 'reward') return `It is over. ${cap(next[0]!.name)} lies open ahead.`;
  return `${cap(here.name)} is behind you.`;
}

export function QuestPathScreen({ inst, a }: { inst: Instance; a: Adventure }) {
  const worldId = inst.worldId;
  const me = useSession((s) => s.identity ?? '');
  const party = useParty(inst.partyId);
  const members = usePartyMembers(inst.partyId);
  const heroes = useWorldHeroes(worldId);
  const names = useNames(worldId);
  const colours = usePlayerColours(worldId);
  const wounds = useQuestWounds(inst.id);
  const pings = useQuestPings(inst.id);
  const clock = useWorldClock(worldId);
  const push = useToasts((s) => s.push);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<number | undefined>();
  const [readTo, setReadTo] = useState<{ key: string; i: number }>({ key: '', i: 0 });
  const zoomRef = useRef<HTMLDivElement>(null);
  const curtain = usePathCurtain();
  const g = a.siteGraph;
  const season = parseSeasonKey(new URLSearchParams(window.location.search).get('season') ?? undefined) ?? seasonKey(clock?.season);
  const scene: Scene = a.scene ?? 'Greenwood';
  const seed = Math.imul(Number(inst.id % 2147483647n) + 1, 2654435761) >>> 0;
  const board = useMemo(() => generatePath(seed, scene, season, g.sites.map((s) => ({ id: s.id, x: s.x, y: s.y, big: s.kind === 'Boss' || s.kind === 'Gate' || s.kind === 'Sanctum' })), g.edges), [seed, scene, season, g]);
  const isLead = party ? identityHex(party.lead) === me : false;
  const leadOwner = party ? identityHex(party.lead) : undefined;
  const leadName = party ? names[identityHex(party.lead)] ?? 'the lead' : 'the lead';
  const inRoom = inst.state === INST.IN_ENCOUNTER;
  const open = inst.state === INST.LOADED ? openNeighbours(g, inst.currentSite, inst.cleared) : [];
  const here = siteById(g, inst.currentSite);
  const retry = inst.state === INST.LOADED && !!here && (here.kind === 'Stop' || here.kind === 'Boss') && !inst.cleared.includes(here.id);
  const ahead = useMemo(() => stillAhead(g, inst.currentSite), [g, inst.currentSite]);
  const heroById = new Map(heroes.map((h) => [h.id.toString(), h]));
  const deployed = [...members].filter((m) => m.deployed).sort((x, y) => (x.id < y.id ? -1 : 1));
  const partyHeroes = deployed.map((m) => heroById.get(m.heroId.toString())).filter((h): h is HeroRow => !!h);
  const mine: BandHero[] = partyHeroes.filter((h) => identityHex(h.owner) === me);

  // docs/113 QP7: the route waits on this screen's walk into a stop
  const setPathInstance = curtain.setPathInstance;
  useEffect(() => { setPathInstance(inst.id); return () => setPathInstance(undefined); }, [inst.id, setPathInstance]);
  const encId = inst.encounterId ?? undefined;
  const released = encId !== undefined && curtain.released.includes(encId.toString());
  const closing = useRef<string | undefined>(undefined);
  const zoomInto = (site: number) => {
    if (encId === undefined || released || closing.current === encId.toString()) return;
    closing.current = encId.toString();
    const z = zoomRef.current, node = z?.querySelector<HTMLElement>(`[data-stop="${site}"] .fk-path__stop-icon`);
    if (z && node) {
      const zr = z.getBoundingClientRect(), nr = node.getBoundingClientRect();
      z.style.transformOrigin = `${nr.left + nr.width / 2 - zr.left}px ${nr.top + nr.height / 2 - zr.top}px`;
      z.dataset.zooming = 'true';
    }
    playSfx('cine.rise');
    curtain.close();
    const id = encId;
    setTimeout(() => usePathCurtain.getState().release(id), CURTAIN_CLOSE_MS);
  };
  const onArrive = (site: number, walked: boolean) => {
    // the leader is at the stop whose encounter just opened (or, back on the map with it still open, at once)
    if (inst.state === INST.IN_ENCOUNTER && site === inst.currentSite && (walked || !released)) zoomInto(site);
  };
  // an encounter opened with no walk to watch (a stop tried again): straight in
  const zoomLater = useRef(zoomInto);
  zoomLater.current = zoomInto;
  useEffect(() => {
    if (!inRoom || encId === undefined || released) return;
    const t = setTimeout(() => zoomLater.current(inst.currentSite), 1600);
    return () => clearTimeout(t);
  }, [inRoom, encId, released, inst.currentSite]);

  const travel = async (id: number) => {
    if (!isLead) { push(`Waiting for ${leadName} to choose the way.`); return; }
    setBusy(true);
    await rpc.travelToSite(inst.id, id);
    setBusy(false);
  };
  const abandon = async () => {
    const ok = await confirmDialog({ eyebrow: inst.title, title: 'Abandon the quest?', text: 'The Chronicle records it; the party keeps a token of XP.', confirmLabel: 'Abandon', cancelLabel: 'Press on', danger: true });
    if (ok) void rpc.abandonAdventure(inst.id);
  };

  // the dialogue: the asker's pitch at the gate, then the stop's own line
  const stop = `${inst.id}:${inst.currentSite}:${inst.cleared.length}`;
  const atStart = inst.currentSite === g.start && inst.visited.length <= 1;
  const lines: Array<{ speaker: string; text: string; portrait: ReactNode }> = [
    ...(atStart ? [{ speaker: a.asker, text: a.pitch.replace('{asker}', a.asker), portrait: <OryxIcon name="item_scroll" scale={3} /> }] : []),
    ...(here && !inRoom ? [{ speaker: titled(here.name), text: stopLine(here, g, inst, open), portrait: <OryxIcon name={STOP[stopKind(here)].icon} scale={3} /> }] : []),
  ];
  const lineIx = readTo.key === stop ? readTo.i : 0;
  const line = lines[lineIx];
  const shown = siteById(g, selected ?? open[0] ?? inst.currentSite);
  const woundOf = (h: HeroRow) => wounds.find((w) => w.heroId === h.id);
  const stops = g.sites.filter((s) => s.kind === 'Stop').length;

  return (
    <main className="fk-path" data-testid="localmap-screen" data-path="true" data-instance-id={inst.id.toString()} data-instance-state={inst.state} data-current-site={inst.currentSite} data-scene={scene}>
      <PathMap a={a} inst={inst} board={board} open={open} ahead={ahead} canTravel={isLead && !inRoom} busy={busy} selected={selected} onSelect={setSelected}
        onTravel={(id) => void travel(id)} party={partyHeroes} leadOwner={leadOwner} colours={colours} me={me} names={names} pings={pings} zoomRef={zoomRef} onArrive={onArrive} />

      <header className="fk-path__bar">
        <div className="fk-path__lead">
          <div className="fk-path__plate fk-hit">
            <span className="fk-path__eyebrow">{a.side ? 'Side quest' : 'Main quest'} · {titled(a.location)} · {SCENE_WORDS[scene]}</span>
            {clock && <AlmanacChip clock={clock} className="fk-path__date" />}
            <h2 className="fk-path__title" data-testid="adventure-title">{inst.title}</h2>
            <span className="fk-path__stats">Asked by {a.asker} · {stops} stops, then the boss · lead {leadName}{isLead ? ' (you)' : ''}</span>
          </div>
          <div className="fk-path__navrow fk-hit"><ShellNav /><ShellName label={false} /></div>
        </div>
        <span className="fk-path__spacer" />
        <TownBand worldId={worldId} heroes={mine} />
        <div className="fk-path__aside fk-hit">
          {isLead && inst.state <= INST.IN_ENCOUNTER && <Button variant="danger" small disabled={inRoom} title={inRoom ? 'finish or abandon the encounter first' : 'Abandon the quest'} onClick={() => void abandon()} data-testid="abandon-adventure">Abandon</Button>}
          <ShellMenuButton />
        </div>
      </header>

      <Dock worldId={worldId} partyId={inst.partyId} />

      <aside className="fk-path__right">
        <Panel variant="solid" className="fk-path__panel fk-path__party" label="The party" data-testid="path-party">
          <div className="fk-path__section"><span className="fk-path__label">The party</span><span className="fk-path__hint">no rest on the road</span></div>
          <ul className="fk-path__heroes">
            {partyHeroes.map((h) => {
              const w = woundOf(h);
              const max = w?.hpMax ?? 0;
              const left = w ? Math.max(1, w.hpMax - w.lost) : 0;
              return (
                <li key={h.id.toString()} className="fk-path__hero" style={{ ['--fk-seat' as string]: playerColour(colours.get(identityHex(h.owner))) } as CSSProperties} data-testid="path-hero" data-hero-name={h.name} data-hp={w ? left : ''} data-hp-max={w ? max : ''}>
                  <HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={2} title={h.name} />
                  <span className="fk-path__hero-name">{h.name}</span>
                  {w && w.lost > 0
                    ? <Meter kind="hp" size="sm" className="fk-path__hp" label="HP" value={left} max={max} format={(v, m) => `${v} / ${m}`} />
                    : <span className="fk-path__whole">unhurt</span>}
                </li>
              );
            })}
          </ul>
        </Panel>
        {shown && (
          <Panel variant="solid" className="fk-path__panel fk-path__card" label="Stop" data-testid="stop-card">
            <div className="fk-path__card-top">
              <span className={`fk-path__tag fk-path__tag--${stopKind(shown)}`}>{STOP[stopKind(shown)].label}</span>
              <Badge tone={shown.id === inst.currentSite ? 'gold' : open.includes(shown.id) ? 'green' : inst.cleared.includes(shown.id) ? 'grey' : ahead.has(shown.id) ? 'blue' : 'grey'}>
                {shown.id === inst.currentSite ? 'the party is here' : open.includes(shown.id) ? 'open' : inst.cleared.includes(shown.id) ? 'cleared' : ahead.has(shown.id) ? 'ahead' : 'passed by'}
              </Badge>
            </div>
            <h3 className="fk-path__site-name" data-testid="current-site">{titled(shown.name)}</h3>
            <p className="fk-path__blurb">{stopKind(shown) === 'boss' && a.battle?.leader ? `${a.battle.leader}, ${a.battle.title}, waits here${a.battle.foes ? ` with ${a.battle.foes.charAt(0).toLowerCase()}${a.battle.foes.slice(1)}` : ''}.` : STOP[stopKind(shown)].blurb}</p>
            {open.includes(shown.id) && isLead && !inRoom && <Button primary block disabled={busy} onClick={() => void travel(shown.id)} data-testid="travel-selected">Go to {titled(shown.name)}</Button>}
          </Panel>
        )}
      </aside>

      <div className="fk-path__foot">
        {line && <DialogueBox key={`${stop}:${lineIx}`} className="fk-path__dialogue" speaker={line.speaker} text={line.text} portrait={line.portrait} more={lineIx < lines.length - 1} onAdvance={() => setReadTo({ key: stop, i: lineIx + 1 })} data-testid="map-dialogue" />}
        <div className="fk-path__ways fk-hit" data-testid="open-paths">
          {inRoom ? <span className="fk-path__label">Into {here ? titled(here.name) : 'the encounter'}…</span>
            : retry && isLead ? <Button primary small onClick={() => void travel(inst.currentSite)} data-testid="travel-button" data-site-id={inst.currentSite}>Try {here ? titled(here.name) : 'it'} again</Button>
            : open.length > 0 && isLead ? (
              <>
                <span className="fk-path__label">{open.length > 1 ? 'Choose the way' : 'Onward'}</span>
                {open.map((id) => { const s = siteById(g, id)!; const k = stopKind(s); return (
                  <Button key={id} small primary disabled={busy} onClick={() => void travel(id)} data-testid="travel-button" data-site-id={id} title={STOP[k].blurb}>
                    <OryxIcon name={STOP[k].icon} scale={1} /> {STOP[k].label}: {titled(s.name)}
                  </Button>
                ); })}
              </>
            ) : !isLead ? <span className="fk-path__hint" data-testid="waiting-for-lead">Waiting for {leadName} to choose the way.</span> : null}
        </div>
      </div>
    </main>
  );
}

/** docs/113 QP7: the black between the path and an encounter: down as the map zooms in, up once the encounter is in. */
export function PathCurtain() {
  const phase = usePathCurtain((s) => s.phase);
  const screen = useRoute((s) => s.screen);
  useEffect(() => {
    const c = usePathCurtain.getState();
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    // the encounter is in: hold the black a moment while its board draws, then lift it
    if (phase === 'black' && screen !== 'localmap') timers.push(setTimeout(c.open, CURTAIN_HOLD_MS));
    if (phase === 'opening') timers.push(setTimeout(c.done, CURTAIN_OPEN_MS));
    // never leave a player in the dark should the encounter not come
    if (phase === 'closing' || phase === 'black') timers.push(setTimeout(c.open, PATH_HOLD_MAX_MS + 1500));
    return () => timers.forEach(clearTimeout);
  }, [phase, screen]);
  return phase === 'idle' ? null : <div className={`fk-path-curtain fk-path-curtain--${phase}`} style={{ ['--close-ms' as string]: `${CURTAIN_CLOSE_MS}ms`, ['--open-ms' as string]: `${CURTAIN_OPEN_MS}ms` } as CSSProperties} data-testid="path-curtain" data-phase={phase} aria-hidden="true" />;
}
